
import { UserGroupIcon } from "@heroicons/react/24/outline";
import { StatusBadge } from "../../../../shared/components/badges/StatusBadge";
import TabbedForm from "../../../../shared/components/forms/FormTabs";
import VillageExpandRow from "../components/VillageExpandRow";
import { VillageService } from "../../../../shared/services/villageService";
import apiService from "../../../../shared/services/apiService";

/* -------------------- OPTION LOADERS -------------------- */
 export const getDistrictOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns/districts?isDropdown');
    const districts = res?.data?.districts || res?.districts || res?.data || [];
    return districts.map((item) => ({
      value: item.value || item.id || item._id,
      label: item.label || item.name,
    }));
  } catch (error) {
    console.warn('Failed to load districts from dropdowns API', error);
    return [];
  }
};

// Taluka options loader - requires districtId
const getTalukaOptions = async (districtId) => {
  if (!districtId) {
    return [];
  }
  try {
    const res = await apiService.getAll(`dropdowns/talukas/${districtId}`);
    const talukas = res?.data?.talukas || res?.talukas || res?.data || [];
    return talukas.map((item) => ({
      value: item.value || item.id || item._id,
      label: item.label || item.name,
    }));
  } catch (error) {
    console.warn('Failed to load talukas from dropdowns API', error);
    return [];
  }
};

export const getStatusOptions = () =>
  Promise.resolve([
    { label: "Active", value: "ACTIVE" },
    { label: "Inactive", value: "INACTIVE" },
  ]);

// Taluka filter options for filtering villages by taluka
export const getTalukaFilterOptions = async () => {
  try {
    const res = await apiService.getAll('talukas?page=1&limit=100&isDropdown=true');
    const talukas =
      res?.data?.data?.talukas ||
      res?.data?.talukas ||
      res?.data?.data ||
      res?.data ||
      res?.talukas ||
      [];

    return talukas.map(t => ({
      value: t.value || t.id || t._id,
      label: t.label || t.name
    }));
  } catch (error) {
    console.warn('Failed to load talukas for filter', error);
    return [];
  }
};

/* -------------------- CONFIG -------------------- */
export const villageConfig = {
  title: "Village",
  endpoint: "admin/villages",
  crudEndpoint: "villages",
  storageKey: "villages",
  icon: UserGroupIcon,
  useUrlNavigation: true,
  basePath: '/admin/village',

  // Custom filter for Taluka
  customFilters: [
    {
      key: 'talukaId',
      label: 'Taluka',
      placeholder: 'All Talukas',
      optionsLoader: getTalukaFilterOptions
    },
      {
      key: 'districtId',
      label: 'District',
      placeholder: 'All Districts',
      optionsLoader: getDistrictOptions
    }
  ],

  /* ---------- TABLE ---------- */
  columns: [
    { key: "name", label: "Village", render: (i) => i.name },
    { key: "taluka", label: "Taluka", render: (i) => i.taluka?.name || "-" },
    { key: "district", label: "District", render: (i) => i.district?.name || "-" },
    { key: "status", label: "Status", render: (i) => <StatusBadge status={i.status} /> },
  ],

  /* ---------- FORM ---------- */
  formFields: [
    { name: "name", label: "Village Name", type: "text", required: true, section: "basic" },

    /* District select - FIRST */
    {
      name: "districtId",
      label: "District",
      type: "select",
      section: "basic",
      required: true,
      placeholder: "Select District",
      optionsLoader: getDistrictOptions,
      renderValue: (value, item) => item?.district?.name || value?.label || value || '—',
    },

    /* Taluka select - DEPENDENT on District */
    {
      name: "talukaId",
      label: "Taluka",
      type: "select",
      section: "basic",
      required: true,
      placeholder: "Please select district first",
      dependsOn: "districtId",
      optionsLoader: getTalukaOptions,
      renderValue: (value, item) => item?.taluka?.name || value?.label || value || '—',
    },

    { name: "postalCode", label: "Postal code", type: "text", required: true, section: "basic" },
    { name: "contactPerson", label: "Contact Person", type: "text", section: "contact", required: true },
    { name: "phone", label: "Phone", type: "tel", section: "contact", required: true },
    { name: "email", label: "Email", type: "email", section: "contact", required: true },
    { name: "officeAddress", label: "Office Address", type: "textarea", section: "contact", required: true, fullWidth: true },
  ],

  formState: { activeTab: "basic" },

  /* ---------- CUSTOM HANDLERS ---------- */
  customHandlers: {
    /* ---- EDIT / VIEW ---- */
    onView: async (item) => {
      const res = await VillageService.getById(item.id);
      const data = res?.data || res;

      return {
        ...data,
        // Set districtId from district object
        districtId: data.district?.id || data.districtId || null,
        // Set talukaId from taluka object
        talukaId: data.taluka?.id || data.talukaId || null,
      };
    },

    /* ---- FORM TABS + DISTRICT → TALUKA DEPENDENCY ---- */
    renderFormSections: (
      formFields,
      formData,
      handleFormChange,
      mode,
      renderFormField,
      activeTab,
      setActiveTab
    ) => {
      const tabsConfig = { basic: { label: "Village Info" }, contact: { label: "Contact Info" } };

      const wrappedRenderFormField = (field) => {
        // Handle District change - clear Taluka when district changes
        if (field.name === "districtId") {
          return renderFormField({
            ...field,
            onChange: async (selectedValue) => {
              console.log("Selected District:", selectedValue);
              handleFormChange("districtId", selectedValue);
              // Clear talukaId when district changes
              handleFormChange("talukaId", null);
            },
          });
        }

        // Handle Taluka - load options based on districtId
        if (field.name === "talukaId") {
          const districtId = formData?.districtId?.value || formData?.districtId;
          return renderFormField({
            ...field,
            disabled: !districtId,
            placeholder: districtId ? "Select Taluka" : "Please select district first",
            optionsLoader: () => getTalukaOptions(districtId),
          });
        }

        return renderFormField(field);
      };

      return (
        <TabbedForm
          formFields={formFields}
          formData={formData}
          handleFormChange={handleFormChange}
          renderFormField={wrappedRenderFormField}
          tabsConfig={tabsConfig}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      );
    },

    /* ---- EXPANDED ROW ---- */
    renderExpandedRow: (item) => <VillageExpandRow item={item} />,

    /* ---- SUBMIT FORM DATA ---- */
    processFormData: (formData) => {
      console.log("Submitting form data:", formData);
      return {
        name: formData.name,
        districtId: formData.districtId?.value || formData.districtId,
        postalCode: formData.postalCode,
        talukaId: formData.talukaId?.value || formData.talukaId,
        contactPerson: formData.contactPerson,
        phone: formData.phone,
        email: formData.email,
        officeAddress: formData.officeAddress,
        status: formData.status || "ACTIVE",
      };
    },
  },
};

export default villageConfig;
